import {
	type DepositableV3Assets,
	DepositableV3AssetsBase,
	MIN_MARGIN_AMOUNT_V3,
	V3_SYNTH_KEYS,
	ZERO_WEI,
} from '@kwenta/sdk/constants'

import { formatDollars, formatNumber, stripZeros, truncateNumbers } from '@kwenta/sdk/utils'
import Wei, { wei } from '@kwenta/wei'
import capitalize from 'lodash/capitalize'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { PerpsProvider } from '@kwenta/sdk/types'
import { TransactionButton } from 'components/Button/Button'
import ErrorView from 'components/ErrorView'
import InputWithAssetSelector from 'components/Input/InputWithAssetSelector'
import SegmentedControl from 'components/SegmentedControl'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import { DEFAULT_USDC_DECIMALS, DEPOSIT_LEFT_FOR_GAS } from 'constants/defaults'
import { EVENT_NAME } from 'constants/matomoEvents'
import { PROVIDER_CHAIN_NAMES, providerChainIcon } from 'constants/network'
import { useAnalytics } from 'hooks/useAnalytics'
import { DrawerTab } from 'sections/accounts/constants'
import { setOpenModalWithParams } from 'state/app/reducer'
import { selectShowModalWithParams } from 'state/app/selectors'
import {
	selectV3DepositableBalances,
	selectV3DepositableBalancesInUSD,
} from 'state/balances/selectors'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import {
	selectAvailableMargin,
	selectIsApprovingCrossDeposit,
	selectIsSubmittingIsolatedTransfer,
} from 'state/futures/selectors'
import { depositWrappedTokenCrossMargin } from 'state/futures/snxPerpsV3/actions'
import {
	selectDepositAllowances,
	selectProviderSupportsZap,
	selectV3DepositOptions,
	selectV3MaxDepositAmounts,
} from 'state/futures/snxPerpsV3/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectAbstractionWalletEnabled, selectIsSubAccountMode } from 'state/wallet/selectors'

const PERCENT_OPTIONS = ['25%', '50%', '75%', '100%']

const FullWidthSelectorButtons = ({
	onSelect,
	options,
}: { onSelect: (index: number) => void; options: string[] }) => {
	return (
		<SelectorButtonsContainer>
			<FullWidthSegmentedControl onChange={onSelect} styleType="pill-button" values={options} />
		</SelectorButtonsContainer>
	)
}

type Props = {
	defaultAsset?: DepositableV3AssetsBase
	buttonSize?: 'small' | 'medium'
}

const DepositCrossMargin = memo(({ defaultAsset, buttonSize = 'medium' }: Props) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { trackDepositFlow, trackManageAccount } = useAnalytics()
	const provider = useAppSelector(selectPerpsProvider)
	const isSubAccountMode = useAppSelector(selectIsSubAccountMode)
	const zapSupported = useAppSelector(selectProviderSupportsZap)
	const allowances = useAppSelector(selectDepositAllowances)
	const v3Balances = useAppSelector(selectV3DepositableBalances)
	const submitting = useAppSelector(selectIsSubmittingIsolatedTransfer)
	const approving = useAppSelector(selectIsApprovingCrossDeposit)
	const modalParams = useAppSelector(selectShowModalWithParams)?.params
	const maxDeposits = useAppSelector(selectV3MaxDepositAmounts)
	const allDepositOptions = useAppSelector(selectV3DepositOptions)
	const availableMargin = useAppSelector(selectAvailableMargin)
	const v3BalancesInUSD = useAppSelector(selectV3DepositableBalancesInUSD)
	const abstractionWalletEnabled = useAppSelector(selectAbstractionWalletEnabled)
	const [selectedAsset, setSelectedAsset] = useState<DepositableV3Assets>(
		defaultAsset ?? DepositableV3AssetsBase.USDC
	)
	const [amount, setAmount] = useState('')

	const depositOptions = useMemo(() => {
		if (provider === PerpsProvider.SNX_V3_BASE) return allDepositOptions
		// USDC no longer supported for arb deposits
		return allDepositOptions.filter((o) => o !== 'USDC')
	}, [allDepositOptions, provider])

	const onSelectPercent = useCallback(
		(index: number) => {
			const percent = PERCENT_OPTIONS[index].replace('%', '')
			let balance = selectedAssetBalance
			if (selectedAsset === 'ETH' && !abstractionWalletEnabled) {
				balance = Wei.max(selectedAssetBalance.sub(DEPOSIT_LEFT_FOR_GAS), ZERO_WEI)
			}
			const margin = wei(balance).div(100).mul(percent)
			setAmount(stripZeros(truncateNumbers(margin, assetDecimals)))
		},
		[abstractionWalletEnabled, selectedAsset]
	)

	// biome-ignore lint/correctness/useExhaustiveDependencies: Need to set only once on mount
	useEffect(() => {
		const { asset } = Object.entries(v3BalancesInUSD).reduce(
			(acc, [asset, value]) => {
				if (depositOptions.includes(asset as DepositableV3Assets)) {
					return wei(value).gt(acc.value) ? { asset, value: wei(value) } : acc
				}
				return acc
			},
			{ asset: '', value: wei(0) }
		)

		if (asset === '') {
			return
		}

		setSelectedAsset(asset as DepositableV3Assets)
	}, [])

	useEffect(() => {
		const asset = modalParams?.asset
		if (V3_SYNTH_KEYS.includes(asset)) {
			setSelectedAsset(asset)
		}
	}, [modalParams?.asset])

	const minDeposit = useMemo(() => {
		// TODO: Update min deposit amount for cross margin
		return wei(0)
	}, [])

	const assetDecimals = useMemo(() => {
		return selectedAsset === 'USDC' ? DEFAULT_USDC_DECIMALS : 18
	}, [selectedAsset])

	const maxDeposit = useMemo(() => {
		if (selectedAsset === 'USDC') {
			return wei('100000000000')
		}

		return wei(maxDeposits?.[selectedAsset] ?? 0)
	}, [maxDeposits, selectedAsset])

	const requiresApproval = useMemo(() => {
		const spender = zapSupported ? 'marginEngine' : 'spotProxy'
		return allowances[selectedAsset]?.[spender]?.lt(amount || 0)
	}, [allowances, amount, selectedAsset, zapSupported])

	const selectedAssetBalance = useMemo(
		() => wei(v3Balances[selectedAsset] || 0),
		[selectedAsset, v3Balances]
	)

	const depositRequired = useMemo(() => {
		return selectedAssetBalance.lte(minDeposit)
	}, [minDeposit, selectedAssetBalance])

	const isOverMaxDeposit = useMemo(() => {
		if (!amount) return false
		return wei(amount).gt(maxDeposit)
	}, [amount, maxDeposit])

	const isAmountInvalid = useMemo(() => {
		if (!amount) {
			return true
		}
		const amtWei = wei(amount)
		return (
			amtWei.eq(0) || amtWei.gt(selectedAssetBalance) || amtWei.lt(minDeposit) || isOverMaxDeposit
		)
	}, [amount, selectedAssetBalance, minDeposit, isOverMaxDeposit])

	const depositDisabled = useMemo(() => {
		if (submitting || approving || isSubAccountMode) {
			return true
		}
		return isAmountInvalid
	}, [submitting, approving, isSubAccountMode, isAmountInvalid])

	const onSelectAsset = useCallback((asset: string) => {
		setAmount('')
		setSelectedAsset(asset as DepositableV3AssetsBase)
	}, [])

	const switchToDeposit = useCallback(() => {
		trackManageAccount(EVENT_NAME.CLICK_MANAGE_ACCOUNT)
		dispatch(
			setOpenModalWithParams({
				type: 'manage_wallet',
				params: { tab: DrawerTab.DEPOSIT, asset: selectedAsset },
			})
		)
	}, [dispatch, trackManageAccount, selectedAsset])

	const handleSetMax = useCallback(() => {
		let balance = selectedAssetBalance
		if (selectedAsset === 'ETH' && !abstractionWalletEnabled) {
			balance = Wei.max(selectedAssetBalance.sub(DEPOSIT_LEFT_FOR_GAS), ZERO_WEI)
		}
		setAmount(stripZeros(truncateNumbers(balance, assetDecimals)))
	}, [selectedAsset, selectedAssetBalance, assetDecimals, abstractionWalletEnabled])

	const onDepositOrApprove = useCallback(async () => {
		trackDepositFlow(EVENT_NAME.SUBMIT_DEPOSIT)

		dispatch(
			depositWrappedTokenCrossMargin({
				amount: wei(amount),
				token: selectedAsset,
				closeOnComplete: true,
			})
		)
	}, [amount, dispatch, trackDepositFlow, selectedAsset])

	return (
		<FlexDivCol rowGap="30px">
			<FlexDivCol rowGap="10px">
				<BalanceContainer onClick={handleSetMax}>
					<BalanceText>{t('futures.market.trade.margin.modal.balance')}:</BalanceText>
					<BalanceText>
						<span>
							{formatNumber(selectedAssetBalance, { suggestDecimals: true, floor: true })}
						</span>
					</BalanceText>
				</BalanceContainer>
				<InputWithAssetSelector
					small={true}
					value={truncateNumbers(amount, assetDecimals)}
					setValue={setAmount}
					selectedAsset={selectedAsset}
					onSelectAsset={onSelectAsset}
					assets={depositOptions}
				/>
				<FullWidthSelectorButtons onSelect={onSelectPercent} options={PERCENT_OPTIONS} />
			</FlexDivCol>

			<FlexDivCol rowGap="20px">
				{isOverMaxDeposit && (
					<ErrorView
						messageType="error"
						message={''}
						messageWithIcon={
							<Trans
								i18nKey="futures.market.trade.margin.modal.deposit.max-deposit"
								values={{ maxAmount: formatDollars(maxDeposit), asset: selectedAsset }}
							/>
						}
					/>
				)}
				{depositRequired && !abstractionWalletEnabled && (
					<ErrorView
						messageType="info"
						message={''}
						messageWithIcon={
							<Trans
								i18nKey="futures.market.trade.margin.modal.deposit.deposit-to-account"
								components={[
									providerChainIcon(provider, { width: 18, height: 18 }),
									<b key={provider} />,
								]}
								values={{ dest: capitalize(PROVIDER_CHAIN_NAMES[provider]) }}
							/>
						}
						containerStyle={{
							cursor: 'pointer',
							display: 'flex',
							flexDirection: 'row',
							columnGap: '4px',
							backgroundColor: 'rgba(0, 82, 255, 0.25)',
							borderColor: 'rgba(0, 82, 255, 0.20)',
							marginBottom: '0px',
						}}
						onClick={switchToDeposit}
					/>
				)}
				{abstractionWalletEnabled && (
					<>
						<Divider height={1} divider />
						<FlexDivCol rowGap="10px">
							<FlexDivRowCentered>
								<Body color="secondary">
									{t('futures.market.trade.margin.modal.transfer.synthetix-account')}
								</Body>
								<Body color="primary">
									<Body color="secondary" type="span">
										{formatNumber(selectedAssetBalance, { suggestDecimals: true, floor: true })}{' '}
										{selectedAsset}
									</Body>
									<StyledArrow />
									{isAmountInvalid
										? formatNumber(selectedAssetBalance, {
												suggestDecimals: true,
												floor: true,
											})
										: formatNumber(selectedAssetBalance.sub(wei(amount === '' ? '0' : amount)), {
												suggestDecimals: true,
												floor: true,
											})}{' '}
									{selectedAsset}
								</Body>
							</FlexDivRowCentered>
							<FlexDivRowCentered>
								<Body color="secondary">
									{t('futures.market.trade.margin.modal.transfer.perps-v3-account')}
								</Body>
								<Body color="secondary">
									{isAmountInvalid
										? formatNumber(wei(availableMargin), {
												suggestDecimals: true,
												floor: true,
											})
										: formatNumber(wei(availableMargin), {
												suggestDecimals: true,
												floor: true,
											})}{' '}
									USDC
									<StyledArrow />
									<Body color="primary" type="span">
										{isAmountInvalid
											? formatNumber(wei(availableMargin), {
													suggestDecimals: true,
													floor: true,
												})
											: formatNumber(wei(availableMargin).add(wei(amount === '' ? '0' : amount)), {
													suggestDecimals: true,
													floor: true,
												})}{' '}
										USDC
									</Body>
								</Body>
							</FlexDivRowCentered>
						</FlexDivCol>
					</>
				)}
			</FlexDivCol>

			{wei(availableMargin).lt(MIN_MARGIN_AMOUNT_V3) && (
				<MinimumAmountDisclaimer>
					<Trans
						i18nKey="futures.market.trade.margin.modal.deposit.disclaimer"
						values={{ minMargin: formatDollars(MIN_MARGIN_AMOUNT_V3, { minDecimals: 0 }) }}
					/>
				</MinimumAmountDisclaimer>
			)}

			<TransactionButton
				data-testid="futures-market-trade-deposit-margin-button"
				disabled={depositDisabled}
				loading={submitting || approving}
				fullWidth
				size={buttonSize}
				onClick={onDepositOrApprove}
				variant="solid-highlight"
			>
				{abstractionWalletEnabled
					? requiresApproval
						? t('futures.market.trade.margin.modal.deposit.approve-and-transfer')
						: t('futures.market.trade.margin.modal.deposit.transfer-button')
					: requiresApproval
						? t('futures.market.trade.margin.modal.deposit.approve-and-deposit')
						: t('futures.market.trade.margin.modal.deposit.button')}
			</TransactionButton>
			{isSubAccountMode && (
				<ErrorView
					messageType="warn"
					message={t('futures.market.trade.confirmation.modal.disabled-deposit-delegation')}
					containerStyle={{ margin: '16px 0 0 0' }}
				/>
			)}
		</FlexDivCol>
	)
})

export default DepositCrossMargin

const BalanceContainer = styled(FlexDivRowCentered)`
	cursor: pointer;
	p {
		margin: 0;
	}
`

const BalanceText = styled.p<{ $gold?: boolean }>`
	color: ${(props) =>
		props.$gold ? props.theme.colors.selectedTheme.yellow : props.theme.colors.selectedTheme.gray};
	span {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

const MinimumAmountDisclaimer = styled.div`
	padding: 10px;
	border-radius: 8px;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.disclaimer.background};
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.disclaimer.color};
	font-size: 13px;
	text-align: center;
`

const Divider = styled(Spacer)`
	width: 100%;
`

const SelectorButtonsContainer = styled.div`
	width: 100%;
	display: flex;
`

const FullWidthSegmentedControl = styled(SegmentedControl)`
	width: 100%;
	font-size: 12px;
	& > div {
		width: 100%;
		display: flex;
	}
	
	& button {
		flex: 1;
		font-size: 12px;
		height: 25px;
	}
`

const StyledArrow = styled.span`
	::before {
		content: '➞';
		color: ${(props) => props.theme.colors.common.secondaryGray};
		font-size: 13px;
		padding: 0px 3px;
		font-weight: 700;
	}
`
